import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Loader2, Search, X } from 'lucide-react';
import { Avatar, Button, Dropdown, DropdownOption } from '../ui';
import { OrgMemberOption, RFQRole } from '../../types';

/**
 * The people picker behind sharing: type a colleague's name, arrow to them,
 * Enter picks them. Picking STAGES — the person becomes a chip in the field —
 * and Share is what actually grants access, to everyone staged at once.
 * Handing someone the ability to email suppliers and accept an award deserves
 * a deliberate second step, not a single stray click.
 *
 * Everyone in the org stays in the results, including the people who already
 * have access: their row says so and cannot be chosen. A name that silently
 * disappeared from a search would read as "they are not on your team", which
 * is a different and untrue answer.
 */
export interface MemberSearchProps {
  members: OrgMemberOption[];
  /** `members` already narrowed by `query` — the hook owns the filtering. */
  matches: OrgMemberOption[];
  staged: OrgMemberOption[];
  onToggleStaged: (userId: string) => void;
  onUnstage: (userId: string) => void;
  onUnstageLast: () => void;
  query: string;
  onQueryChange: (query: string) => void;
  role: RFQRole;
  onRoleChange: (role: RFQRole) => void;
  onShare: () => void;
  sharing: boolean;
  disabled?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}

/**
 * One grantable role today. It is still a control rather than fixed text
 * because choosing what someone can do is part of the decision, and the
 * description is where that decision is explained.
 */
const ROLE_OPTIONS: DropdownOption[] = [
  { value: 'EDITOR', label: 'Editor', description: 'Can change everything except who has access' },
];

const displayName = (member: OrgMemberOption) => member.name ?? member.email ?? 'Someone on your team';

const standingLabel = (member: OrgMemberOption) =>
  member.existingRole === 'OWNER' ? 'Owner' : 'Already an editor';

const StagedChip: React.FC<{ member: OrgMemberOption; onRemove: () => void; disabled: boolean }> = ({
  member, onRemove, disabled,
}) => (
  <span className="inline-flex max-w-[180px] items-center gap-s2 rounded-round border border-action-border bg-action-bg py-[2px] pl-s3 pr-[3px] text-body-sm text-action-deep">
    <span className="truncate">{displayName(member)}</span>
    <button
      type="button"
      disabled={disabled}
      onClick={onRemove}
      aria-label={`Don't share with ${displayName(member)}`}
      className="inline-flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-round text-action-deep hover:bg-action-border focus:outline-none focus-visible:ring-2 focus-visible:ring-action disabled:opacity-50"
    >
      <X className="h-[11px] w-[11px]" />
    </button>
  </span>
);

export const MemberSearch: React.FC<MemberSearchProps> = ({
  members,
  matches,
  staged,
  onToggleStaged,
  onUnstage,
  onUnstageLast,
  query,
  onQueryChange,
  role,
  onRoleChange,
  onShare,
  sharing,
  disabled = false,
  inputRef,
}) => {
  const listId = React.useId();
  const ownRef = useRef<HTMLInputElement>(null);
  const fieldRef = inputRef ?? ownRef;
  const [activeIndex, setActiveIndex] = useState(0);

  const stagedIds = useMemo(() => new Set(staged.map((m) => m.userId)), [staged]);

  // Arrow keys walk only the rows that can actually be picked, so Enter never
  // lands on a row that would do nothing. The others stay on screen, marked.
  const pickableIndexes = useMemo(
    () => matches.map((m, i) => (m.existingRole ? -1 : i)).filter((i) => i >= 0),
    [matches],
  );

  useEffect(() => setActiveIndex(pickableIndexes[0] ?? -1), [pickableIndexes]);

  const step = (delta: number) => {
    if (pickableIndexes.length === 0) return;
    const at = pickableIndexes.indexOf(activeIndex);
    const next = at < 0 ? 0 : Math.min(Math.max(at + delta, 0), pickableIndexes.length - 1);
    setActiveIndex(pickableIndexes[next]);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      step(1);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      step(-1);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const member = matches[activeIndex];
      if (member && !member.existingRole) onToggleStaged(member.userId);
      return;
    }
    if (event.key === 'Backspace' && !query && staged.length > 0) {
      // Nothing to delete in the field itself, so the key takes back the last
      // pick — the standard behaviour of any chip input.
      event.preventDefault();
      onUnstageLast();
      return;
    }
    if (event.key === 'Escape' && query) {
      // Clear the search first; a second Escape reaches the modal and closes
      // it. Losing a half-typed name to a stray Escape would be its own bug.
      event.preventDefault();
      event.stopPropagation();
      onQueryChange('');
    }
  };

  const emptyRoster = members.length === 0;
  const fieldDisabled = disabled || emptyRoster;

  return (
    <div>
      <div className="flex items-start gap-s3">
        {/* The chips live inside the field, so what you picked and what you are
            typing read as one input rather than two separate lists. */}
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) fieldRef.current?.focus();
          }}
          className={
            'flex min-h-8 min-w-0 flex-1 flex-wrap items-center gap-s2 rounded-control border border-line bg-surface px-s3 py-[3px] ' +
            'transition-colors duration-100 hover:border-line-strong focus-within:border-action focus-within:ring-2 focus-within:ring-action-border ' +
            (fieldDisabled ? 'bg-canvas opacity-50' : '')
          }
        >
          <Search className="h-[13px] w-[13px] shrink-0 text-ink-muted" />
          {staged.map((member) => (
            <StagedChip
              key={member.userId}
              member={member}
              disabled={sharing}
              onRemove={() => onUnstage(member.userId)}
            />
          ))}
          <input
            ref={fieldRef}
            type="text"
            role="combobox"
            aria-expanded={matches.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
            aria-label="Search your team by name or email"
            placeholder={staged.length > 0 ? 'Add someone else…' : 'Search your team by name or email'}
            value={query}
            disabled={fieldDisabled || sharing}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={onKeyDown}
            className="h-[26px] min-w-[140px] flex-1 border-0 bg-transparent p-0 text-body-md text-ink placeholder:text-ink-muted focus:outline-none focus:ring-0 disabled:bg-transparent"
          />
        </div>
        <Dropdown
          options={ROLE_OPTIONS}
          value={role}
          onChange={(next) => onRoleChange(next as RFQRole)}
          ariaLabel="Role for the people you add"
          disabled={fieldDisabled || sharing}
          className="w-[132px] shrink-0"
        />
        <Button
          onClick={onShare}
          disabled={staged.length === 0 || sharing}
          iconLeft={sharing ? <Loader2 className="animate-spin" /> : undefined}
          className="shrink-0"
        >
          {sharing ? 'Sharing…' : 'Share'}
        </Button>
      </div>

      {/* Honest about the disabled button: say what would turn it on. */}
      {staged.length === 0 && !emptyRoster && (
        <p className="mt-s2 text-meta text-ink-muted">
          Pick who you want to share with — I'll add them all when you press Share.
        </p>
      )}

      {emptyRoster ? (
        <p className="mt-s4 text-body-sm text-ink-secondary">
          You're the only person on your team right now, so there's no one for me to share this with yet.
        </p>
      ) : matches.length === 0 ? (
        <p className="mt-s4 text-body-sm text-ink-secondary">
          No one in your org matches “{query.trim()}”. Try part of their name or their email address.
        </p>
      ) : (
        <ul
          id={listId}
          role="listbox"
          aria-multiselectable
          aria-label="Search results"
          className="mt-s3 max-h-[188px] list-none overflow-y-auto rounded-card border border-line-divider p-s2"
        >
          {matches.map((member, index) => {
            const active = index === activeIndex;
            const blocked = Boolean(member.existingRole);
            const picked = stagedIds.has(member.userId);
            return (
              <li
                key={member.userId}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={picked}
                aria-disabled={blocked}
                onMouseEnter={() => !blocked && setActiveIndex(index)}
                onClick={() => !blocked && onToggleStaged(member.userId)}
                className={
                  'flex items-center gap-s3 rounded-control px-s3 py-s2 ' +
                  (blocked ? 'opacity-60' : 'cursor-pointer ') +
                  (active && !blocked ? 'bg-action-bg' : '')
                }
              >
                <Avatar name={displayName(member)} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-row-title text-ink">{displayName(member)}</span>
                  {member.email && member.email !== member.name && (
                    <span className="block truncate text-meta text-ink-muted">{member.email}</span>
                  )}
                </span>
                {blocked ? (
                  <span className="shrink-0 text-meta text-ink-muted">{standingLabel(member)}</span>
                ) : picked ? (
                  <span className="inline-flex shrink-0 items-center gap-s1 text-meta font-medium text-action">
                    <Check className="h-[12px] w-[12px]" />
                    Picked
                  </span>
                ) : (
                  <span className="shrink-0 text-meta text-ink-muted">Pick</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default MemberSearch;
